import { A } from "../assets";
import { Basis, Btn, CONTACT, Eyebrow, Item, More, Page, Peek, TK, href } from "../core";
import { BrainAsk, Matrix, MatrixExplorer, RepriceSim } from "../demos";

export default function ChefBrain() {
  return (
    <Page current="/chef-brain" contact={{ title: <>See your own menu <em>on the matrix.</em></>, lede: "Send a menu and a recent sales export. We return your matrix with the priority moves ranked, and walk you through it on a call." }}>
      <section className="tk-hero tk-hero--paper">
        <div className="tk-wrap tk-hero__grid">
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>Chef Brain · for GMs, F&amp;B directors and owners</Eyebrow>
              <h1 className="tk-h1">Your menu is a P&amp;L. Most kitchens still read it as a <em>list.</em></h1>
            </div>
            <p className="tk-lede">Chef Brain reads your point-of-sale data and places every dish by popularity and contribution margin. It tells you what to protect, reprice, reposition or remove, and what each move is worth before you make it.</p>
            <div className="tk-row">
              <Btn to={CONTACT}>Book a demo</Btn>
              <Btn to={TK.trialUrl} kind="ghost">Start a 15-day trial</Btn>
            </div>
            <ul className="tk-ticks">
              <li>Runs on your own sales data</li>
              <li>Keeps your current POS</li>
              <li>Outlet to group views</li>
            </ul>
          </div>
          <div className="tk-board">
            <div className="tk-board__bar"><img src={A.kmarkInk} alt="" /><span>Chef Brain · command centre</span><span>Illustrative</span></div>
            <div className="tk-board__body">
              <div className="tk-kpis">
                <div className="tk-kpi"><span>Contribution margin</span><b>64.2%</b><i>+1.8 pts on last month</i></div>
                <div className="tk-kpi"><span>Average check, AED</span><b>312</b><i>+4.1%</i></div>
                <div className="tk-kpi"><span>Dishes needing a decision</span><b>7</b><i className="dn">3 lost margin this week</i></div>
              </div>
              <Matrix compact />
            </div>
          </div>
        </div>
      </section>

      <section className="tk-sec tk-sec--bone">
        <div className="tk-wrap">
          <div className="tk-head">
            <Eyebrow>The margin leak</Eyebrow>
            <h2 className="tk-h2">A handful of dishes fund your restaurant. <em>Can you name them?</em></h2>
            <p className="tk-lede">Most menus are written once, priced by instinct, and revised when a supplier raises a cost. The P&amp;L moves every week. The gap between the two is where margin goes quiet.</p>
          </div>
          <div className="tk-grid tk-grid--3">
            <Item icon="chart" title="Popular is not profitable">The dish you sell most is often the one you earn least on. Volume flatters a thin margin, and the kitchen keeps building the roster around it.</Item>
            <Item icon="eye" title="Your best margin is buried">High-margin dishes sit on page three under a heading nobody reads, described in four words. They do not fail on quality. They fail on placement.</Item>
            <Item icon="coins" title="Price moves are guesses">Raise it two dirhams and you might gain margin or lose covers. Without an elasticity read you find out after the quarter closes.</Item>
          </div>
        </div>
      </section>

      <section className="tk-sec tk-sec--paper">
        <div className="tk-wrap tk-split tk-split--wide-right tk-split--top">
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>The method</Eyebrow>
              <h2 className="tk-h2">Every dish, on the <em>two axes that matter.</em></h2>
            </div>
            <p className="tk-body">Chef Brain applies the menu engineering method published by Kasavana and Smith in 1982, which hospitality schools have taught ever since. Most properties run it once a year in a spreadsheet. Chef Brain runs it on live sales, by outlet and by meal period, and attaches an instruction to each group.</p>
            <p className="tk-body">Choose a group to see what Chef Brain tells you to do with it.</p>
          </div>
          <MatrixExplorer />
        </div>
      </section>

      <section className="tk-sec tk-sec--sand">
        <div className="tk-wrap tk-split tk-split--top">
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>Three instruments on top of the matrix</Eyebrow>
              <h2 className="tk-h2">Test the change <em>before you make it.</em></h2>
            </div>
            <div className="tk-stack">
              <Item icon="basket" title="Basket pairs">Which starter pulls which main, which dessert follows which wine, and where a pairing would earn more than a discount.</Item>
              <Item icon="slider" title="Price elasticity">Demand response per dish from your own transaction history. You see the point where a price rise starts costing covers.</Item>
              <Item icon="scatter" title="Repricing simulation">Move a price, change a placement, retire a dish. The simulation returns the projected effect on revenue and contribution.</Item>
              <Item icon="bell" title="Recipe cost alerts">When an ingredient price moves, every dish that uses it is re-costed, and you are told which ones changed group.</Item>
            </div>
          </div>
          <RepriceSim />
        </div>
      </section>

      <section className="tk-sec tk-sec--paper">
        <div className="tk-wrap tk-split tk-split--wide-left tk-split--top">
          <BrainAsk />
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>Chef Brain Chat</Eyebrow>
              <h2 className="tk-h2">Ask it the way you would <em>ask your analyst.</em></h2>
            </div>
            <p className="tk-body">Type a question in plain English and Chef Brain draws the chart and explains what moved. No report request, no waiting until Monday.</p>
            <Item icon="lock" title="Computed, never composed">The language model only plans the question. A deterministic compiler turns that plan into the query, and the figure on screen is calculated from your data. You can open the plan behind any answer.</Item>
            <Item icon="file" title="Send it to tonight's briefing">Pin an answer to the command centre or pass it to the pre-service briefing in one action.</Item>
          </div>
        </div>
      </section>

      <section className="tk-sec tk-sec--ink">
        <div className="tk-wrap">
          <div className="tk-head">
            <Eyebrow>One view for each chair</Eyebrow>
            <h2 className="tk-h2">The same data, read <em>four ways.</em></h2>
          </div>
          <div className="tk-grid tk-grid--4">
            <Item title="General manager">Which outlets are carrying the property this month, and which need attention before the owner asks.</Item>
            <Item title="Director of F&amp;B">Which dishes to protect, reprice, reposition or remove at the next menu change, with the projected effect of each.</Item>
            <Item title="Executive chef">Which recipes moved in cost this week, and what that did to the dishes built on them.</Item>
            <Item title="Owner and finance">Contribution margin by outlet and meal period, on definitions that stay the same across the estate.</Item>
          </div>
        </div>
      </section>

      <section className="tk-sec tk-sec--bone">
        <div className="tk-wrap tk-split tk-split--top">
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>For groups and multi-property estates</Eyebrow>
              <h2 className="tk-h2">Compare outlets <em>like for like.</em></h2>
            </div>
            <p className="tk-body">A group cannot manage what each property measures differently. Chef Brain uses one set of definitions everywhere, so a contribution margin in Dubai means the same thing in Delhi.</p>
            <More to={href("/platform")}>See the platform underneath</More>
          </div>
          <div className="tk-stack">
            <Item icon="file" title="A library of 100 standard F&amp;B reports">Daily trade, item performance, meal-period mix, staff productivity and margin movement, in the same layout at every outlet.</Item>
            <Item icon="layers" title="46 defined measures">Every figure traces to a named measure with one definition, so two people asking the same question get the same answer.</Item>
            <Item icon="users" title="Outlet, property, brand and group" tag="Rolling out">Outlet and multi-branch views are live. Property, brand and group consoles for hotel estates are being released in stages. Ask us for the current schedule.</Item>
          </div>
        </div>
        <Peek left />
      </section>

      <section className="tk-sec tk-sec--paper tk-sec--tight">
        <div className="tk-wrap tk-row" style={{ justifyContent: "space-between" }}>
          <p className="tk-fine"><Basis kind="Illustrative" /> &nbsp;All screens and figures on this page use example data. Your matrix, elasticity curves and answers are built only from your own sales.</p>
          <More to={href("/platform", "pricing")}>Chef Brain is in every plan. See pricing</More>
        </div>
      </section>
    </Page>
  );
}
